import dayjs from 'dayjs';
import { TooltipProps } from 'recharts';
import { BigNum, SpotMarketConfig } from '@velocity-exchange/sdk';
import { cj, cm } from 'src/utils/classNames';
import { VAULT_NOTIONAL_GROWTH_STRATEGY_INFO_TEXT } from 'src/constants/vaults/misc';
import InlineAlert from 'src/components/primitives/InlineAlert/InlineAlert';
import { TOOLTIP_CONTENT_CLASSNAME } from 'src/components/primitives/Tooltip/consts';

export type GraphType = 'apy' | 'pnl' | 'balance' | 'sharePrice' | 'roi';
export type Period = '7d' | '30d' | '90d';

export function getYDomain(minY: number, maxY: number) {
	if (minY >= 0) {
		const difference = maxY - minY;
		const offset = difference * 2; // make the curve look less steep
		const absoluteMinY = Math.max(minY - offset, 0); // shouldn't go below 0

		return [absoluteMinY, 'auto'];
	}

	return [minY, 'auto'];
}

export const VaultHistoryGraphTooltip = ({
	active,
	payload,
	label,
	marketConfig,
	isPnl,
	isPct,
}: TooltipProps<number, string> & {
	marketConfig: SpotMarketConfig;
	isPnl: boolean;
	isPct: boolean;
}) => {
	if (active && payload && payload.length) {
		const date = dayjs.unix(label).format('D MMM YYYY');
		const value = payload[0].value ?? 0;

		const isRemoveDecimals = value >= 100;

		const isProfit = value >= 0;
		const isUsdcAsset = marketConfig.marketIndex === 0;

		return (
			<div className={cj('flex flex-col gap-y-xs', TOOLTIP_CONTENT_CLASSNAME)}>
				<div>{date}</div>
				<div
					className={cm(
						isProfit
							? 'text-text-positive-green-button'
							: 'text-text-negative-red-button'
					)}
				>
					{isPct ? (
						<div>{value.toFixed(2)}%</div>
					) : (
						<>
							{isProfit && isPnl && '+'}
							{`${isUsdcAsset ? '$' : ''}${BigNum.fromPrint(
								isRemoveDecimals ? value.toFixed(0) : value.toString(),
								marketConfig.precisionExp
							).prettyPrint()}${isUsdcAsset ? '' : ` ${marketConfig.symbol}`}`}
						</>
					)}
				</div>
			</div>
		);
	}

	return null;
};

export const VaultGraphMarketLegend = (props: { marketSymbol: string }) => {
	return (
		<span className="flex items-center gap-1 typo-body-2xs text-text-secondary">
			<span>Denominated in</span>
			<span>{props.marketSymbol}</span>
		</span>
	);
};

export const NotionalGrowthStrategyAlert = () => {
	return (
		<InlineAlert
			variant="info"
			title={VAULT_NOTIONAL_GROWTH_STRATEGY_INFO_TEXT}
		/>
	);
};
